import { ReactNode } from 'react';
import { useAuthorities } from '@/hooks/useAuthorities';
import { Authority } from '@/types/auth';

interface AuthorityGuardProps {
  children: ReactNode;
  /** Single authority required */
  authority?: Authority;
  /** Multiple authorities - user needs ANY of these */
  anyOf?: Authority[];
  /** Multiple authorities - user needs ALL of these */
  allOf?: Authority[];
  /** Fallback content when user doesn't have permission */
  fallback?: ReactNode;
}

/**
 * Component to conditionally render content based on user authorities
 * 
 * @example
 * // Show content only if user has specific authority
 * <AuthorityGuard authority="ADMIN_CAMPAIGN_MGMT_VIEW">
 *   <CampaignList />
 * </AuthorityGuard>
 * 
 * @example
 * // Show content if user has any of the authorities
 * <AuthorityGuard anyOf={["ADMIN_CAMPAIGN_MGMT_VIEW", "ADMIN_CAMPAIGN_MGMT_UPDATE"]}>
 *   <CampaignSection />
 * </AuthorityGuard>
 * 
 * @example
 * // Show content if user has all authorities
 * <AuthorityGuard allOf={["ADMIN_CAMPAIGN_MGMT_VIEW", "ADMIN_CAMPAIGN_MGMT_UPDATE"]}>
 *   <AdminPanel />
 * </AuthorityGuard>
 */
export function AuthorityGuard({
  children,
  authority,
  anyOf,
  allOf,
  fallback = null,
}: AuthorityGuardProps) {
  const { hasAuthority, hasAnyAuthority, hasAllAuthorities } = useAuthorities();

  let hasPermission = false;

  if (authority) {
    hasPermission = hasAuthority(authority);
  } else if (anyOf && anyOf.length > 0) {
    hasPermission = hasAnyAuthority(...anyOf);
  } else if (allOf && allOf.length > 0) {
    hasPermission = hasAllAuthorities(...allOf);
  } else {
    // No authority specified, allow by default
    hasPermission = true;
  }

  return hasPermission ? <>{children}</> : <>{fallback}</>;
}
